import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ImageUpload } from '@/components/ui/image-upload';
import { Plus, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BillLineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  account_id?: string;
}

interface BillFormProps {
  billId?: string;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export const BillForm: React.FC<BillFormProps> = ({ billId, onSuccess, trigger }) => {
  const [open, setOpen] = useState(false);
  const [billNumber, setBillNumber] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [taxRate, setTaxRate] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [filePath, setFilePath] = useState('');
  const [lineItems, setLineItems] = useState<BillLineItem[]>([
    { id: '1', description: '', quantity: 1, unit_price: 0, total: 0 }
  ]);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: suppliers, isLoading: suppliersLoading, refetch: refetchSuppliers } = useQuery({
    queryKey: ['suppliers-active', open],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, display_name')
        .eq('is_active', true)
        .order('display_name');
      
      if (error) throw error;
      return data;
    },
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['chart-of-accounts-expense', open],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('id, name, code')
        .eq('is_active', true)
        .like('code', '6%') // Expense accounts
        .order('code');
      
      if (error) throw error;
      return data;
    },
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { id: Date.now().toString(), description: '', quantity: 1, unit_price: 0, total: 0 }
    ]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };

  const updateLineItem = (id: string, field: keyof BillLineItem, value: any) => {
    setLineItems(lineItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        // Recalculate total
        if (field === 'quantity' || field === 'unit_price') {
          updated.total = updated.quantity * updated.unit_price;
        }
        return updated;
      }
      return item;
    }));
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
  const taxAmount = taxRate ? subtotal * (parseFloat(taxRate) / 100) : 0;
  const grandTotal = subtotal + taxAmount;

  const createBillMutation = useMutation({
    mutationFn: async (billData: any) => {
      const { data, error } = await supabase
        .from('bills')
        .insert(billData)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast({ title: 'Bill created successfully!' });
      setOpen(false);
      resetForm();
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: 'Error creating bill',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const resetForm = () => {
    setBillNumber('');
    setSupplierId('');
    setIssueDate('');
    setDueDate('');
    setCurrency('EUR');
    setTaxRate(null);
    setNotes('');
    setFilePath('');
    setLineItems([{ id: '1', description: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!supplierId) {
      toast({
        title: 'Validation Error',
        description: 'Please select a supplier',
        variant: 'destructive'
      });
      return;
    }

    const billData = {
      bill_number: billNumber || null,
      supplier_id: supplierId,
      issue_date: issueDate || null,
      due_date: dueDate || null,
      currency,
      tax_rate: taxRate ? parseFloat(taxRate) : null,
      description: notes || null,
      file_path: filePath || null,
      grand_total: grandTotal,
      amount: grandTotal,
      status: 'draft',
      line_items: lineItems
    };

    createBillMutation.mutate(billData);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Bill
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Bill</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="billNumber">Bill Number</Label>
              <Input
                id="billNumber"
                value={billNumber}
                onChange={(e) => setBillNumber(e.target.value)}
                placeholder="Optional bill number"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier *</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent position="popper" side="bottom" align="start" className="z-[1000] bg-popover text-popover-foreground shadow-lg">
                  {suppliers?.length ? suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.display_name}
                    </SelectItem>
                  )) : (
                    <SelectItem disabled value="__no_suppliers">No suppliers found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="RON">RON (lei)</SelectItem>
                  <SelectItem value="PLN">PLN (zł)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="issueDate">Issue Date</Label>
              <Input
                id="issueDate"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="taxRate">Tax Rate</Label>
              <Select value={taxRate || 'none'} onValueChange={(val) => setTaxRate(val === 'none' ? null : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tax rate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Tax</SelectItem>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="6">6%</SelectItem>
                  <SelectItem value="13">13%</SelectItem>
                  <SelectItem value="24">24%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Line Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Line
              </Button>
            </div>
            
            {lineItems.map((item, index) => (
              <Card key={item.id}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                    <div className="md:col-span-2">
                      <Label>Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Item description"
                      />
                    </div>
                    
                    <div>
                      <Label>Account</Label>
                      <Select 
                        value={item.account_id || ''} 
                        onValueChange={(value) => updateLineItem(item.id, 'account_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Account" />
                        </SelectTrigger>
                        <SelectContent position="popper" side="bottom" align="start" className="z-[1000] bg-popover text-popover-foreground shadow-lg">
                          {accounts?.length ? accounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.code} - {account.name}
                            </SelectItem>
                          )) : (
                            <SelectItem disabled value="__no_accounts">No accounts found</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Qty</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div>
                      <Label>Unit Price</Label>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label>Total</Label>
                        <div className="text-sm font-medium p-2 bg-muted rounded">
                          {item.total.toFixed(2)}
                        </div>
                      </div>
                      {lineItems.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLineItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Totals */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2 max-w-sm ml-auto">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{subtotal.toFixed(2)} {currency}</span>
                </div>
                {taxRate && (
                  <div className="flex justify-between">
                    <span>Tax ({taxRate}%):</span>
                    <span>{taxAmount.toFixed(2)} {currency}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-bold">
                  <span>Grand Total:</span>
                  <span>{grandTotal.toFixed(2)} {currency}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Attach File (PDF/Image)</Label>
                <ImageUpload
                  value={filePath}
                  onChange={setFilePath}
                  bucket="invoice-pdfs"
                  variant="square"
                  size="sm"
                />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createBillMutation.isPending}>
              {createBillMutation.isPending ? 'Creating...' : 'Create Bill'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};